/**
 * The tenancy routes of the route table (`routes.ts` spreads these into
 * `ROUTES`): who am I, the vendor's tenant registry, and a tenant admin's
 * users, groups and memberships (PLAN §11, D13, D14).
 *
 * All tenant-level: `permissionScope: 'tenant'`, so the permission must be held
 * through a tenant-wide group — `tenant.admin` for one studio does not make you
 * a tenant admin. A path id from another tenant answers `not-found`, the same
 * as one that does not exist (D13).
 *
 * Typed with `satisfies` against a type-only import, so `routes.ts` can import
 * this module without a runtime cycle and each entry keeps its concrete zod
 * types (`z.infer<typeof ROUTES.createGroup.body>` is the handler's input).
 */
import type { RouteDef } from './routes.js';
import {
  acceptInviteRequestSchema,
  acceptInviteResponseSchema,
  createdTenantResponseSchema,
  inspectInviteRequestSchema,
  inspectInviteResponseSchema,
  invitedUserResponseSchema,
  createGroupRequestSchema,
  createTenantRequestSchema,
  createUserRequestSchema,
  groupPathSchema,
  groupResponseSchema,
  groupWithMembersResponseSchema,
  listGroupsResponseSchema,
  listTenantsResponseSchema,
  listUsersResponseSchema,
  meResponseSchema,
  memberPathSchema,
  updateGroupRequestSchema,
  userPathSchema,
  userResponseSchema,
} from './tenancy.js';

export const TENANCY_ROUTES = {
  getMe: {
    id: 'getMe',
    method: 'GET',
    path: '/me',
    tag: 'tenancy',
    summary: 'Who am I',
    description: 'The caller, their tenant, every permission they hold, and where each applies (one grant per group).',
    params: null,
    query: null,
    body: null,
    response: meResponseSchema,
    successStatus: 200,
    permission: null,
    permissionScope: 'tenant',
    errors: ['not-found'],
  },
  createTenant: {
    id: 'createTenant',
    method: 'POST',
    path: '/tenants',
    tag: 'vendor',
    summary: 'Create a tenant',
    description:
      'Creates a tenant with its default groups (Admin, Producer/Editor, Operator, Viewer; D14) and its first admin, whose Cognito invitation is suppressed. All or nothing: `conflict` if the tenant id or the admin email exists. Vendor tenant only.',
    params: null,
    query: null,
    body: createTenantRequestSchema,
    response: createdTenantResponseSchema,
    successStatus: 201,
    permission: 'vendor.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'conflict', 'payload-too-large'],
  },
  listTenants: {
    id: 'listTenants',
    method: 'GET',
    path: '/tenants',
    tag: 'vendor',
    summary: 'List tenants',
    description: 'Every tenant in this regional deployment (D12). Vendor tenant only.',
    params: null,
    query: null,
    body: null,
    response: listTenantsResponseSchema,
    successStatus: 200,
    permission: 'vendor.admin',
    permissionScope: 'tenant',
    errors: [],
  },
  createUser: {
    id: 'createUser',
    method: 'POST',
    path: '/users',
    tag: 'tenancy',
    summary: 'Invite a user',
    description:
      'Creates a Cognito user in the caller’s tenant (Cognito’s own invitation is suppressed), adds it to `groupIds`, and sends ACP’s branded invite email: the tenant’s name and a one-time link to set a password (valid 72 h; the link is never in a response). The user is `invited` until they accept. Every group must exist in the tenant first; `conflict` if the email is taken anywhere in the pool.',
    params: null,
    query: null,
    body: createUserRequestSchema,
    response: invitedUserResponseSchema,
    successStatus: 201,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict', 'payload-too-large'],
  },
  resendInvite: {
    id: 'resendInvite',
    method: 'POST',
    path: '/users/{userId}/invite',
    tag: 'tenancy',
    summary: 'Resend an invite',
    description: 'Sends a fresh invite link to a user who has not accepted yet; the previous link stops working. `conflict` for an active or disabled user.',
    params: userPathSchema,
    query: null,
    body: null,
    response: invitedUserResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found', 'conflict'],
  },
  deactivateUser: {
    id: 'deactivateUser',
    method: 'POST',
    path: '/users/{userId}/deactivate',
    tag: 'tenancy',
    summary: 'Deactivate a user',
    description:
      'Disables the user’s Cognito sign-in and signs them out everywhere, and refuses their tokens from now on; the nodes drop them (and their local sessions) at the next directory sync (D20). Their memberships are kept, so reactivating restores their access. Idempotent. You cannot deactivate yourself, nor the last active member of the Admin group (`conflict`).',
    params: userPathSchema,
    query: null,
    body: null,
    response: userResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found', 'conflict'],
  },
  reactivateUser: {
    id: 'reactivateUser',
    method: 'POST',
    path: '/users/{userId}/reactivate',
    tag: 'tenancy',
    summary: 'Reactivate a user',
    description: 'Enables a deactivated user’s sign-in again; they return as `active`, or `invited` if they never accepted their invite. Idempotent.',
    params: userPathSchema,
    query: null,
    body: null,
    response: userResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  inspectInvite: {
    id: 'inspectInvite',
    method: 'POST',
    path: '/invites/inspect',
    tag: 'tenancy',
    summary: 'Look at an invite',
    description:
      'The set-password page’s first call: whose invite a link is (email, tenant name) and until when. Unauthenticated — the token is the credential — and rate-limited per source address; a mistyped, used, superseded or expired token is `unauthorized`, all alike. A valid invite into a suspended tenant is `suspended`, with the reason (the invite stays usable once the tenant is reactivated).',
    params: null,
    query: null,
    body: inspectInviteRequestSchema,
    response: inspectInviteResponseSchema,
    successStatus: 200,
    permission: null,
    permissionScope: 'tenant',
    auth: 'none',
    errors: ['bad-request', 'validation-failed', 'payload-too-large'],
  },
  acceptInvite: {
    id: 'acceptInvite',
    method: 'POST',
    path: '/invites/accept',
    tag: 'tenancy',
    summary: 'Accept an invite',
    description:
      'Sets the invited user’s password (permanent) and makes them `active`; the link is spent. Unauthenticated — the token is the credential — and rate-limited per source address; a bad token is `unauthorized` (five wrong secrets kill the invite). A password the pool refuses is `validation-failed`; a valid invite into a suspended tenant is `suspended` and is not spent. Then sign in as usual.',
    params: null,
    query: null,
    body: acceptInviteRequestSchema,
    response: acceptInviteResponseSchema,
    successStatus: 200,
    permission: null,
    permissionScope: 'tenant',
    auth: 'none',
    errors: ['bad-request', 'validation-failed', 'payload-too-large'],
  },
  listUsers: {
    id: 'listUsers',
    method: 'GET',
    path: '/users',
    tag: 'tenancy',
    summary: 'List users',
    description: 'The tenant’s users, by email, each with their status and the ids of their groups.',
    params: null,
    query: null,
    body: null,
    response: listUsersResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: [],
  },
  deleteUser: {
    id: 'deleteUser',
    method: 'DELETE',
    path: '/users/{userId}',
    tag: 'tenancy',
    summary: 'Delete a user',
    description: 'Deletes the Cognito user and every membership; returns the deleted user. You cannot delete yourself, nor the last member of the Admin group (`conflict`).',
    params: userPathSchema,
    query: null,
    body: null,
    response: userResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found', 'conflict'],
  },
  createGroup: {
    id: 'createGroup',
    method: 'POST',
    path: '/groups',
    tag: 'tenancy',
    summary: 'Create a group',
    description: 'A group grants `permissions` within `scope`: the whole tenant, or named sites and studios (D14). `vendor.*` permissions are grantable only in the vendor tenant.',
    params: null,
    query: null,
    body: createGroupRequestSchema,
    response: groupResponseSchema,
    successStatus: 201,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'conflict', 'payload-too-large'],
  },
  listGroups: {
    id: 'listGroups',
    method: 'GET',
    path: '/groups',
    tag: 'tenancy',
    summary: 'List groups',
    description: 'The tenant’s groups, by name.',
    params: null,
    query: null,
    body: null,
    response: listGroupsResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: [],
  },
  getGroup: {
    id: 'getGroup',
    method: 'GET',
    path: '/groups/{groupId}',
    tag: 'tenancy',
    summary: 'Get a group',
    description: 'The group and its members’ user ids.',
    params: groupPathSchema,
    query: null,
    body: null,
    response: groupWithMembersResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  updateGroup: {
    id: 'updateGroup',
    method: 'PATCH',
    path: '/groups/{groupId}',
    tag: 'tenancy',
    summary: 'Update a group',
    description:
      'Changes any of name, permissions, scope. The Admin group (and the vendor’s Vendor Admin group) must keep its admin permissions tenant-wide (`conflict`). With `ifUpdatedAt`, a group changed since that time is `conflict` and nothing is written (D35).',
    params: groupPathSchema,
    query: null,
    body: updateGroupRequestSchema,
    response: groupResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict', 'payload-too-large'],
  },
  deleteGroup: {
    id: 'deleteGroup',
    method: 'DELETE',
    path: '/groups/{groupId}',
    tag: 'tenancy',
    summary: 'Delete a group',
    description: 'Deletes the group and its memberships; returns the deleted group. The Admin group cannot be deleted (`conflict`).',
    params: groupPathSchema,
    query: null,
    body: null,
    response: groupResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found', 'conflict'],
  },
  addGroupMember: {
    id: 'addGroupMember',
    method: 'PUT',
    path: '/groups/{groupId}/members/{userId}',
    tag: 'tenancy',
    summary: 'Add a member',
    description: 'Idempotent. Both the group and the user must be in the caller’s tenant. Returns the group and its members.',
    params: memberPathSchema,
    query: null,
    body: null,
    response: groupWithMembersResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  removeGroupMember: {
    id: 'removeGroupMember',
    method: 'DELETE',
    path: '/groups/{groupId}/members/{userId}',
    tag: 'tenancy',
    summary: 'Remove a member',
    description: 'Idempotent. The Admin group (and the Vendor Admin group) keeps at least one member (`conflict`). Returns the group and its members.',
    params: memberPathSchema,
    query: null,
    body: null,
    response: groupWithMembersResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found', 'conflict'],
  },
} as const satisfies Record<string, RouteDef>;
